import { PostalAddress } from '@/interfaces/CustomerInterface/CustomerInterface';
import { InvoiceData } from '@/redux/invoiceManaged/invoiceManagedInterface';
import { OrderDetailsResponseContent, PostOrder, PostRetake } from '@/redux/orderManaged/orderManagedInterface';
import { getTodayDate } from '@/utils/dateHelper';

import { getRoadType } from './formHelper';
import { Dashboard } from '@/redux/operation/operationsInterface';

interface BuildPostOrderParams {
  dataOrderInvoice: PostOrder;
  nameValue?: string;
  surnameValue?: string;
  identity?: string;
  addressData?: {
    city?: string;
    province?: string;
    postal_code?: string;
    address?: string;
    country?: string;
  };
  addresShippingData?: {
    country?: string;
    city?: string;
    province?: string;
    postal_code?: string;
    address?: string;
    phone?: string;
  };
  ocrId?: string;
  birthDate?: string;
  nationality?: string;
  gender?: string;
}

export const buildPostOrder = ({
  dataOrderInvoice,
  nameValue,
  surnameValue,
  identity,
  addressData,
  ocrId,
  birthDate,
  nationality,
  gender,
  addresShippingData,
}: BuildPostOrderParams): PostOrder => {
  const {
    process_type,
    order_date,
    invoice_id,
    cart,
    term,
    commercial_offer_id,
    total_products,
    total_price,
    shipping,
    product_type,
    input_mode,
    platform,
    url_callback,
    transaction_id,
    view_mode,
    applicant,
    is_preferred,
  } = dataOrderInvoice;

  const postOrder: PostOrder = {
    process_type,
    order_date,
    cart: {
      id: cart.id,
      ...(cart.cart_price && { cart_price: cart.cart_price }),
      ...(cart.shipping_price && { shipping_price: cart.shipping_price }),
      products: cart.products.length > 0
        ? cart.products.map((product) => ({
          id: product.id || 'id',
          reference: product.reference || 'ref',
          // Si no existe el valor o es null de name, se le asigna "N/A"
          name: product.name || 'N/A',
          // Si no existe el valor o es null de qty, se le asigna 1
          qty: product.qty || 1,
          price: product.price || 0,
          ...(product.attributes && { attributes: product.attributes }),
          ...(product.isChecked && { isChecked: product.isChecked }),
          ...(product.refundQty && { refundQty: product.refundQty }),
        }))
        : [
          {
            id: 'id',
            reference: 'ref',
            name: 'N/A',
            qty: 1,
            price: 0,
          },
        ],
    },
    total_products,
    total_price,
    applicant: {
      id: applicant.id || identity || cart.id,
      name: nameValue || applicant.name,
      last_name: surnameValue || applicant.last_name,
      email: applicant.email,
      ...(identity
        ? { document_id: identity }
        : applicant.document_id && { document_id: applicant.document_id }),
      ...(applicant.group && { group: applicant.group }),
      // eslint-disable-next-line no-mixed-operators
      ...(gender && { gender } || applicant.gender && { gender: applicant.gender }),
      // eslint-disable-next-line max-len, no-mixed-operators
      ...(nationality && { nationality } || applicant.nationality && { nationality: applicant.nationality }),
      birth_date: birthDate || applicant.birth_date,
      ...(applicant.ip && { ip: applicant.ip }),
      billing_address: {
        ...(addressData?.country
          ? { country: addressData.country }
          : applicant.billing_address.country && { country: applicant.billing_address.country }),
        city: addressData?.city || applicant.billing_address.city,
        ...(addressData?.province
          ? { province: addressData.province }
          : applicant.billing_address.province && { province: applicant.billing_address.province }),
        postal_code: addressData?.postal_code || applicant.billing_address.postal_code,
        address: addressData?.address || applicant.billing_address.address,
        ...(applicant.billing_address.phone && { phone: applicant.billing_address.phone }),
      },
      shipping_address: (() => {
        if (addresShippingData) {
          return {
            country: addresShippingData.country === '' ? 'España' : addresShippingData.country,
            city: addresShippingData.city,
            province: addresShippingData.province,
            postal_code: addresShippingData.postal_code,
            address: addresShippingData.address,
            phone: addresShippingData.phone,
          };
        } if (applicant.shipping_address) {
          return {
            country: applicant.shipping_address.country,
            city: applicant.shipping_address.city,
            province: applicant.shipping_address.province,
            postal_code: applicant.shipping_address.postal_code,
            address: applicant.shipping_address.address,
            phone: applicant.shipping_address.phone,
          };
        }
        return undefined;
      })(),
    },
    ...(is_preferred !== undefined && { is_preferred }),
    ...(invoice_id && { invoice_id }),
    ...(term && { term }),
    ...(commercial_offer_id && { commercial_offer_id }),
    ...(shipping && { shipping }),
    ...(product_type && { product_type }),
    ...(input_mode && { input_mode }),
    platform: platform || 'API',
    ...(url_callback && { url_callback }),
    ...(transaction_id && { transaction_id }),
    ...(view_mode && { view_mode }),
    ...(ocrId && { ocr_id: ocrId }),
  };

  return postOrder;
};

export const buildDataOrderInvoice = (orderData: OrderDetailsResponseContent, invoiceData: InvoiceData, additionalData?: any): PostOrder | null => {
  const { isDocument, processType } = additionalData;
  if (!isDocument && !orderData) {
    console.error('Faltan datos de orderData');
    return null;
  }
  if (isDocument && !invoiceData) {
    console.error('Faltan datos de invoiceData');
    return null;
  }

  const objectData = isDocument ? invoiceData.third_party_response : orderData;

  return {
    process_type: processType || 'MANAGED',
    view_mode: 'IFRAME',
    input_mode: 'CUSTOMER_BLOCKED',
    ...(objectData.is_preferred !== undefined && { is_preferred: objectData.is_preferred }),
    order_date: isDocument ? getTodayDate() : objectData.order_date || '',
    ...(objectData.platform && { platform: objectData.platform }),
    ...(objectData.transaction_id && { transaction_id: objectData.transaction_id }),
    ...(objectData.term && { term: objectData.term }),
    total_products: objectData.cart.products.length || 1,
    total_price: objectData.total_price,
    ...(objectData.url_callback && { url_callback: objectData.url_callback }),
    ...(objectData.view_mode && { view_mode: objectData.view_mode }),
    cart: objectData.cart,
    applicant: objectData.applicant,
    ...(objectData.invoice_id && { invoice_id: objectData.invoice_id }),

  };
};

export function formatPostRetake(retrievedOrderData: OrderDetailsResponseContent | undefined): PostRetake | null {
  if (retrievedOrderData) {
    return {
      platform: retrievedOrderData.platform || 'API',
      order_date: retrievedOrderData.order_date || getTodayDate(),
      term: retrievedOrderData.term,
      ...(retrievedOrderData.term && { term: retrievedOrderData.term }),
      total_products: retrievedOrderData.total_products || 1,
      total_price: retrievedOrderData.total_price,
      applicant: retrievedOrderData.applicant,
      cart: retrievedOrderData.cart,
      ...(retrievedOrderData.url_callback && { url_callback: retrievedOrderData.url_callback }),
      ...(retrievedOrderData.transaction_id && { transaction_id: retrievedOrderData.transaction_id }),
      view_mode: 'IFRAME',
      ...(retrievedOrderData.product_type && { product_type: retrievedOrderData.product_type }),
      ...(retrievedOrderData.input_mode && { input_mode: retrievedOrderData.input_mode }),

    };
  }
  return null;
}

const formatAddress = (toFormatAddress: string) => {
  let aux = '';
  const dir1Match = toFormatAddress.match(/^(.*?)\s*(\d+)/);
  const street = dir1Match?.[1];
  const number = dir1Match?.[2].replace(/^0+/, '');
  if (street) {
    const dir1 = `${street} ${number}`.trim();
    const auxDir = toFormatAddress.match(/\d+\s+(.*?)\s+(?=-)/)?.[1];
    if (auxDir) {
      aux = auxDir;
    }
    const dir3 = toFormatAddress.split('-').slice(-2).map((part) => part.trim()).join(', ');
    return { formattedAddress: `${dir1}, ${dir3}`, aux };
  }
  return { formattedAddress: undefined, aux };
};

export const getAddressDataFromDefaultValue2 = async (
  defaultValue: string,
  // eslint-disable-next-line no-async-promise-executor
): Promise<PostalAddress> => new Promise(async (resolve) => {
  if (!defaultValue) {
    console.warn('No default value provided.');
    resolve({
      address: '',
      city: '',
      number: '',
      postal_code: '',
      road_type: '',
      province: '',
      aux: '',
    });
    return;
  }
  const result = formatAddress(defaultValue);

  try {
    const { AutocompleteSuggestion }: any = await google.maps.importLibrary('places');

    // Obtener sugerencias de autocompletado
    const { suggestions } = await AutocompleteSuggestion.fetchAutocompleteSuggestions({
      input: defaultValue,
      includedRegionCodes: ['ES'], // Restringir a España
    });
    if (!suggestions && suggestions.length == 0) {
      console.warn('No suggestions found.');
      resolve({
        address: '',
        city: '',
        number: '',
        postal_code: '',
        road_type: '',
        province: '',
        aux: '',
      });
      return;
    }
    const place = suggestions[0].placePrediction.toPlace();
    try {
      await place.fetchFields({ fields: ['addressComponents'] });
      const components = place.addressComponents || [];
      const addressComponents = {
        street_number: components.find((c: any) => c.types.includes('street_number'))?.longText || '',
        route: components.find((c: any) => c.types.includes('route'))?.longText || '',
        city: components.find((c: any) => c.types.includes('locality'))?.longText || '',
        postal_code: components.find((c: any) => c.types.includes('postal_code'))?.longText || '',
        province: components.find((c: any) => c.types.includes('administrative_area_level_1'))?.longText || '',
        country: components.find((c: any) => c.types.includes('country'))?.longText || '',
        // si hay subpremise lo añadimos a aux
        aux: components.find((c: any) => c.types.includes('subpremise'))?.longText || '',
      };

      const roadType = addressComponents.route.split(' ')[0] || '';
      const foundAddress = `${addressComponents.route} ${addressComponents.street_number}`;

      const addressData: PostalAddress = {
        address: foundAddress,
        city: addressComponents.city,
        number: addressComponents.street_number,
        postal_code: addressComponents.postal_code,
        road_type: getRoadType(roadType),
        province: addressComponents.province || addressComponents.city,
        aux: addressComponents.aux || result.aux || '',
        country: addressComponents.country || '',
      };
      resolve(addressData);
    } catch (error) {
      console.error('Error fetching fields:', error);
      resolve({
        address: '',
        city: '',
        number: '',
        postal_code: '',
        road_type: '',
        province: '',
        aux: '',
      });
    }
  } catch (error) {
    console.error('Error fetching address data:', error);
    resolve({
      address: '',
      city: '',
      number: '',
      postal_code: '',
      road_type: '',
      province: '',
      aux: '',
    });
  }
});
